import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

import { TEMPLATES, TEMAS, PALETTES } from "./templates";
import { getRsvpCssVars, getRsvpTheme } from "../../utils/rsvpTheme";
import RsvpClosedState from "../../components/RsvpClosedState";
import { getEventExternalUrlFromHostname } from "../../utils/publicEventUrl";
import { getBackground } from "../../utils/layoutUtils";

// ---------- helpers ----------

const DEFAULT_DOCUMENT_TITLE = "Gestor de Eventos";

function criarPessoa(id, nome = "") {
  return { id, nome, crianca: false, idade: "" };
}

let proximoId = 1;

function novoId() {
  return proximoId++;
}

function getPublicFeedbackTheme(evento) {
  const layoutConfig = evento?.layoutConfig ?? null;
  const baseTheme = TEMAS[evento?.templateId] || TEMAS.original;
  const tema = getRsvpTheme(baseTheme, layoutConfig);
  const palette = PALETTES[evento?.templateId] || {};
  const pageBackground = getBackground(layoutConfig)
    ?? layoutConfig?.rsvpBg
    ?? palette.pageBg
    ?? tema.sectionBg
    ?? "#f8fafc";
  const cardBackground = layoutConfig?.rsvpSectionBg
    ?? layoutConfig?.rsvpPeopleBg
    ?? palette.rsvpBg
    ?? palette.cardBg
    ?? tema.itemBg
    ?? "#ffffff";
  const inputTheme = tema.people?.input ?? tema.inputExtra ?? {};
  const titleColor = tema.titulo?.color ?? palette.titleColor ?? tema.labelColor ?? "#1f2937";
  const textColor = tema.labelColor ?? palette.textColor ?? "#475569";
  const mutedColor = tema.mutedTextColor ?? palette.subTextColor ?? textColor;
  const detailBackground = tema.message?.sectionBg
    ?? tema.recadoBg
    ?? tema.sectionBg
    ?? palette.rsvpInnerBg
    ?? "rgba(248,250,252,0.9)";
  const detailTextColor = tema.message?.textColor ?? tema.sectionTitleColor ?? textColor;
  const detailTitleColor = tema.message?.titleColor ?? detailTextColor;
  const fontFamily = tema.people?.fontFamily ?? palette.fontFamily ?? undefined;
  const titleFontFamily = tema.titulo?.fontFamily ?? fontFamily;

  return {
    tema,
    palette,
    pageBackground,
    cardBackground,
    inputTheme,
    titleColor,
    textColor,
    mutedColor,
    detailBackground,
    detailTextColor,
    detailTitleColor,
    fontFamily,
    titleFontFamily,
    buttonStyle: tema.submit ?? {},
  };
}

// ---------- página pública ----------

export default function EventoPublico() {
  const { slug: routeSlug, codigoConfirmacao: routeCodigoConfirmacao } = useParams();
  const externalUrl = routeSlug ? null : getEventExternalUrlFromHostname();
  const [searchParams] = useSearchParams();
  const previewTemplate = searchParams.get("preview");
  const queryCodigoConfirmacao = searchParams.get("convite");
  const codigoConfirmacao = routeCodigoConfirmacao || queryCodigoConfirmacao;
  const [evento, setEvento] = useState(null);
  const [convidados, setConvidados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [resultado, setResultado] = useState(null); // { sucessos, erros }

  const layoutConfig = evento?.layoutConfig ?? null;

  useEffect(() => {
    const endpoint = codigoConfirmacao
      ? `${process.env.REACT_APP_API}/convidado/codigo/${encodeURIComponent(codigoConfirmacao)}`
      : routeSlug
        ? `${process.env.REACT_APP_API}/evento/slug/${routeSlug}`
        : externalUrl
          ? `${process.env.REACT_APP_API}/evento/url-externa?url=${encodeURIComponent(externalUrl)}`
          : null;

    if (!endpoint) {
      setErro("Evento não encontrado. Verifique o link.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErro(null);
    setEvento(null);
    setConvidados([]);
    axios
      .get(endpoint)
      .then((res) => {
        if (codigoConfirmacao) {
          setEvento(res.data.evento);
          setConvidados(res.data.convidados || (res.data.convidado ? [res.data.convidado] : []));
          return;
        }

        setEvento(res.data);
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 404) setErro("Evento não encontrado. Verifique o link.");
        else if (status === 401) setErro("Acesso não autorizado (401).");
        else if (status) setErro(`Erro ${status} ao carregar o evento.`);
        else setErro(`Erro de rede: ${err.message}`);
      })
      .finally(() => setLoading(false));
  }, [routeSlug, externalUrl, codigoConfirmacao]);

  useEffect(() => {
    if (evento?.nome) {
      document.title = evento.nome;
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
      };
    }

    if (erro) {
      document.title = "Evento não encontrado";
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
      };
    }

    document.title = loading ? "Carregando evento..." : DEFAULT_DOCUMENT_TITLE;
    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
    };
  }, [evento?.nome, erro, loading]);

  useEffect(() => {
    if (!evento?.imagemUrl) return undefined;

    const links = Array.from(document.querySelectorAll(
      'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'
    ));
    const anteriores = links.map((link) => ({ link, href: link.getAttribute("href") }));

    links.forEach((link) => link.setAttribute("href", evento.imagemUrl));

    return () => {
      anteriores.forEach(({ link, href }) => {
        if (href == null) link.removeAttribute("href");
        else link.setAttribute("href", href);
      });
    };
  }, [evento?.imagemUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-400">Carregando...</p>
      </div>
    );
  }

  if (erro || !evento) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-2xl font-semibold text-gray-600">Evento não encontrado</p>
          <p className="text-gray-400 mt-2">{erro ?? "Verifique o link e tente novamente."}</p>
        </div>
      </div>
    );
  }

  if (!previewTemplate && !evento.isActive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-6">
          <div className="text-5xl mb-4">🔒</div>
          <p className="text-2xl font-semibold text-gray-600">Evento indisponível</p>
          <p className="text-gray-400 mt-2">Este evento não está aceitando confirmações no momento.</p>
        </div>
      </div>
    );
  }

  if (resultado) {
    return <TelaResultado resultado={resultado} evento={evento} />;
  }

  if (!previewTemplate && evento.exigirCodigoConvite && convidados.length === 0) {
    return (
      <CodigoConviteGate
        evento={evento}
        routeSlug={routeSlug}
        onValidado={(data) => {
          setEvento(data.evento);
          setConvidados(data.convidados || []);
          const codigoConfirmacaoValidado = data.codigoConfirmacao || data.convidados?.[0]?.codigoConfirmacao;
          if (!codigoConfirmacaoValidado) return;
          const codigo = encodeURIComponent(codigoConfirmacaoValidado);
          const nextPath = routeSlug ? `/e/${routeSlug}?convite=${codigo}` : `/${codigo}`;
          window.history.replaceState(null, "", nextPath);
        }}
      />
    );
  }

  const Template = TEMPLATES[previewTemplate] || TEMPLATES[evento.templateId] || TEMPLATES.original;

  const activeTemplateId = previewTemplate || evento.templateId;

  return (
    <Template evento={evento} layoutConfig={layoutConfig} templateId={activeTemplateId}>
      <RsvpForm
        evento={evento}
        convidados={convidados}
        templateId={activeTemplateId}
        layoutConfig={layoutConfig}
        onConcluido={setResultado}
      />
    </Template>
  );
}

// ---------- tela de resultado ----------

function TelaResultado({ resultado, evento }) {
  const recusas = resultado.recusas || 0;
  const totalProcessado = resultado.sucessos + recusas;
  const feedbackTheme = getPublicFeedbackTheme(evento);
  const formatarData = (d) =>
    new Date(d).toLocaleDateString("pt-BR", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
      timeZone: "America/Sao_Paulo",
    });

  const titulo = resultado.erros.length > 0 && totalProcessado > 0 ? "Respostas parcialmente registradas" :
    recusas > 0 && resultado.sucessos > 0 ? "Respostas registradas!" :
      recusas > 0 && resultado.sucessos === 0 ? "Resposta registrada" :
    resultado.sucessos === 1 ? "Presença confirmada!" :
    resultado.sucessos > 1 ? "Presenças confirmadas!" :
      resultado.erros.length > 0 ? "Nenhuma presença confirmada" : "Confirmação concluída";

  const textoDetalhe = recusas > 0 && resultado.sucessos > 0
    ? `${resultado.sucessos} ${resultado.sucessos === 1 ? "presença confirmada" : "presenças confirmadas"} e ${recusas} ${recusas === 1 ? "ausência registrada" : "ausências registradas"}.`
    : recusas > 0 && resultado.sucessos === 0 ? "Obrigado por avisar. Sua resposta foi registrada." :
    resultado.sucessos === 1 ? "Sua presença foi confirmada com sucesso." :
    resultado.sucessos > 1 ? `Foram confirmadas ${resultado.sucessos} presenças com sucesso.` :
      resultado.erros.length > 0 ? "Infelizmente, não foi possível confirmar sua presença." : "Todas as confirmações foram processadas.";

  const feedbackIcon = resultado.erros.length > 0
    ? totalProcessado > 0 ? "⚠️" : "❌"
    : resultado.sucessos > 0 ? "🎉" : "💌";
  const localEvento = evento.nomeEspaco ? `${evento.nomeEspaco} — ${evento.localizacao}` : evento.localizacao;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: feedbackTheme.pageBackground, ...getRsvpCssVars(feedbackTheme.tema) }}
    >
      <div
        className="p-10 max-w-md w-full text-center"
        style={{
          background: feedbackTheme.cardBackground,
          borderRadius: feedbackTheme.palette.cardRadius ?? 24,
          border: feedbackTheme.palette.cardBorder ?? feedbackTheme.tema.closedBorder ?? "1px solid rgba(148, 163, 184, 0.24)",
          boxShadow: feedbackTheme.palette.cardShadow ?? "0 18px 45px rgba(15, 23, 42, 0.12)",
          color: feedbackTheme.textColor,
          fontFamily: feedbackTheme.fontFamily,
        }}
      >
        <div className="text-5xl mb-4">{feedbackIcon}</div>
        <h2
          className="text-2xl font-bold mb-2"
          style={{
            color: feedbackTheme.titleColor,
            fontFamily: feedbackTheme.titleFontFamily,
            textShadow: feedbackTheme.tema.titulo?.textShadow,
          }}
        >
          {titulo}
        </h2>
        {(resultado.sucessos > 0 || recusas > 0) && (
          <p className="mb-4" style={{ color: feedbackTheme.mutedColor }}>
            {textoDetalhe}
          </p>
        )}
        {resultado.erros.length > 0 && (
          <div className="text-left bg-red-50 rounded-xl p-4 mb-4 text-sm text-red-700 space-y-1">
            {resultado.erros.map((e, i) => <p key={i}>• {e}</p>)}
          </div>
        )}
        <div
          className="rounded-xl p-4 text-left text-sm space-y-1"
          style={{
            background: feedbackTheme.detailBackground,
            color: feedbackTheme.detailTextColor,
            border: feedbackTheme.tema.closedBorder ?? "1px solid rgba(148, 163, 184, 0.16)",
          }}
        >
          <p><strong style={{ color: feedbackTheme.detailTitleColor }}>Evento:</strong> {evento.nome}</p>
          <p><strong style={{ color: feedbackTheme.detailTitleColor }}>Data:</strong> {formatarData(evento.dataDoEvento)}</p>
          <p><strong style={{ color: feedbackTheme.detailTitleColor }}>Local:</strong> {localEvento}</p>
        </div>
      </div>
    </div>
  );
}

function CodigoConviteGate({ evento, routeSlug, onValidado }) {
  const [codigo, setCodigo] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const feedbackTheme = getPublicFeedbackTheme(evento);

  async function validarCodigo(e) {
    e.preventDefault();
    const codigoLimpo = codigo.trim();
    if (!codigoLimpo) {
      setErro("Informe o código do convite.");
      return;
    }

    setLoading(true);
    setErro(null);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API}/convidado/codigo/${encodeURIComponent(codigoLimpo)}`);
      if (res.data?.evento?.id !== evento.id) {
        setErro("Código não encontrado para este evento.");
        return;
      }

      onValidado(res.data);
    } catch (err) {
      const status = err.response?.status;
      setErro(status === 404 ? "Código não encontrado." : "Não foi possível validar o código.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: feedbackTheme.pageBackground, ...getRsvpCssVars(feedbackTheme.tema) }}
    >
      <div
        className="w-full max-w-sm p-7 shadow-sm"
        style={{
          background: feedbackTheme.cardBackground,
          borderRadius: feedbackTheme.palette.cardRadius ?? 24,
          border: feedbackTheme.palette.cardBorder ?? feedbackTheme.tema.closedBorder ?? "1px solid rgba(148, 163, 184, 0.24)",
          boxShadow: feedbackTheme.palette.cardShadow ?? "0 18px 45px rgba(15, 23, 42, 0.12)",
          color: feedbackTheme.textColor,
          fontFamily: feedbackTheme.fontFamily,
        }}
      >
        <p
          className="text-xs font-semibold uppercase tracking-wide"
          style={{ color: feedbackTheme.mutedColor }}
        >
          Convite restrito
        </p>
        <h1
          className="mt-2 text-2xl font-bold"
          style={{
            color: feedbackTheme.titleColor,
            fontFamily: feedbackTheme.titleFontFamily,
            textShadow: feedbackTheme.tema.titulo?.textShadow,
          }}
        >
          Informe seu código
        </h1>
        <p className="mt-1 text-sm font-medium" style={{ color: feedbackTheme.mutedColor }}>
          {evento.nome}
        </p>
        <p className="mt-3 text-sm" style={{ color: feedbackTheme.textColor }}>
          Informe o código do convite para acessar a página de confirmação.
        </p>

        <form onSubmit={validarCodigo} className="mt-6 space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: feedbackTheme.textColor }}>
              Código
            </label>
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              autoComplete="one-time-code"
              className="rsvp-people-field w-full rounded-xl border px-4 py-3 outline-none focus:ring-2"
              style={{
                background: feedbackTheme.inputTheme.background ?? "#ffffff",
                borderColor: feedbackTheme.inputTheme.borderColor ?? "rgba(148, 163, 184, 0.32)",
                color: feedbackTheme.inputTheme.color ?? feedbackTheme.textColor,
                fontFamily: feedbackTheme.inputTheme.fontFamily ?? feedbackTheme.fontFamily,
                boxShadow: "none",
              }}
              placeholder={routeSlug ? "Digite seu código" : "Ex: hsadfg8"}
              disabled={loading}
            />
          </div>

          {erro && (
            <p className="rounded-lg px-3 py-2 text-sm" style={{ background: "rgba(239, 68, 68, 0.12)", color: "#b91c1c" }}>
              {erro}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-3 text-sm font-semibold disabled:opacity-60"
            style={{
              background: feedbackTheme.buttonStyle.background ?? "#111827",
              color: feedbackTheme.buttonStyle.color ?? "#ffffff",
              borderRadius: feedbackTheme.buttonStyle.borderRadius ?? 16,
              boxShadow: feedbackTheme.buttonStyle.boxShadow ?? "0 10px 24px rgba(15, 23, 42, 0.18)",
              border: feedbackTheme.buttonStyle.border ?? "none",
              letterSpacing: feedbackTheme.buttonStyle.letterSpacing,
              fontFamily: feedbackTheme.buttonStyle.fontFamily ?? feedbackTheme.fontFamily,
            }}
          >
            {loading ? "Validando..." : "Acessar convite"}
          </button>
        </form>
      </div>
    </div>
  );
}

function useMediaQuery(query) {
  const getMatches = () =>
    typeof window !== "undefined" && window.matchMedia(query).matches;

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const media = window.matchMedia(query);
    const handleChange = () => setMatches(media.matches);

    handleChange();
    media.addEventListener("change", handleChange);

    return () => media.removeEventListener("change", handleChange);
  }, [query]);

  return matches;
}

// ---------- temas por template ----------

// ---------- formulário RSVP ----------

function RsvpForm({ evento, convidados = [], templateId, layoutConfig, onConcluido }) {
  const temaBase = TEMAS[templateId] || TEMAS.original;
  const tema = getRsvpTheme(temaBase, layoutConfig);
  const peopleTheme = tema.people ?? {};
  const messageTheme = tema.message ?? {};
  const [pessoas, setPessoas] = useState(() => [criarPessoa(novoId())]);
  const [respostasConvidados, setRespostasConvidados] = useState({});
  const [recado, setRecado] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);
  const layoutConvidadoMobile = useMediaQuery("(max-width: 520px)");
  const conviteNominal = convidados.length > 0;
  const convidadosKey = convidados.map((c) => `${c.id}:${c.statusConfirmacao ?? ""}`).join("|");
  const convidadosPendentes = convidados.filter((c) => !convidadoJaRespondeu(c));
  const convidadosRespondidos = convidados.filter(convidadoJaRespondeu);
  const jaExisteRecadoRegistrado = getRecadosConvidados(convidadosRespondidos).length > 0;
  const todosPendentesRespondidos =
    convidadosPendentes.length > 0 &&
    convidadosPendentes.every((c) => respostaConvidadoFoiInformada(respostasConvidados[c.id]));
  const conviteJaRespondido = conviteNominal && convidadosPendentes.length === 0;

  const prazoEncerrado =
    evento.dataLimiteConfirmacao &&
    new Date() > new Date(evento.dataLimiteConfirmacao);

  useEffect(() => {
    setRespostasConvidados({});
    setAlertMsg(null);
  }, [convidadosKey]);

  if (prazoEncerrado) {
    return (
      <div style={{ ...estilos.formSection, ...getRsvpCssVars(tema), color: tema.formTextColor ?? undefined }}>
        <RsvpClosedState theme={tema} deadline={evento.dataLimiteConfirmacao} />
      </div>
    );
  }

  function convidadoJaRespondeu(convidado) {
    return convidado.statusConfirmacao === 1 || convidado.statusConfirmacao === 2 || convidado.statusConfirmacao === 3;
  }

  function respostaConvidadoFoiInformada(status) {
    return status === 1 || status === 2;
  }

  function labelStatusConvidado(convidado) {
    if (convidado.statusConfirmacao === 1) return "Confirmado";
    if (convidado.statusConfirmacao === 2) return "Não vai";
    if (convidado.statusConfirmacao === 3) return "Talvez";
    return "Pendente";
  }

  function getStatusConvidadoStyle(convidado) {
    if (convidado.statusConfirmacao === 1) return estilos.convidadoStatusConfirmado;
    if (convidado.statusConfirmacao === 2) return estilos.convidadoStatusRecusado;
    if (convidado.statusConfirmacao === 3) return estilos.convidadoStatusTalvez;
    return estilos.convidadoStatusPendente;
  }

  function getResumoConviteRespondido() {
    const confirmados = convidados.filter((c) => c.statusConfirmacao === 1).length;
    const recusados = convidados.filter((c) => c.statusConfirmacao === 2).length;
    const talvez = convidados.filter((c) => c.statusConfirmacao === 3).length;

    if (confirmados === convidados.length) {
      return {
        icon: "🎉",
        titulo: confirmados === 1 ? "Presença confirmada!" : "Presenças confirmadas!",
        texto: confirmados === 1
          ? "Sua presença já foi confirmada com sucesso."
          : `Foram confirmadas ${confirmados} presenças com sucesso.`,
      };
    }

    if (recusados === convidados.length) {
      return {
        icon: "💌",
        titulo: "Resposta registrada",
        texto: "Obrigado por avisar. Este convite já foi respondido.",
      };
    }

    if (talvez === convidados.length) {
      return {
        icon: "💌",
        titulo: "Resposta registrada",
        texto: "Este convite já teve a resposta registrada como talvez.",
      };
    }

    return {
      icon: "💌",
      titulo: "Respostas registradas",
      texto: "Este convite já teve todas as respostas registradas.",
    };
  }

  function getRecadosConvidados(listaConvidados) {
    const recados = listaConvidados
      .map((convidado) => convidado.recado?.trim())
      .filter(Boolean);

    return [...new Set(recados)];
  }

  function renderStatusConvidado(convidado) {
    return (
      <span style={{
        ...estilos.convidadoStatus,
        ...getStatusConvidadoStyle(convidado),
      }}>
        {labelStatusConvidado(convidado)}
      </span>
    );
  }

  function renderRecadosConvidados(listaConvidados) {
    const recados = getRecadosConvidados(listaConvidados);
    if (recados.length === 0) return null;

    return (
      <div style={{
        ...estilos.recadoRespondidoBox,
        background: messageTheme.sectionBg ?? tema.recadoBg,
        color: messageTheme.textColor ?? tema.sectionTitleColor ?? tema.labelColor,
        fontFamily: messageTheme.fontFamily ?? undefined,
      }}>
        <p style={{
          ...estilos.recadoRespondidoTitulo,
          color: messageTheme.titleColor ?? tema.sectionTitleColor ?? tema.labelColor,
        }}>
          {recados.length === 1 ? "Recado enviado" : "Recados enviados"}
        </p>
        {recados.map((recadoEnviado) => (
          <p key={recadoEnviado} style={estilos.recadoRespondidoTexto}>
            {recadoEnviado}
          </p>
        ))}
      </div>
    );
  }

  function renderConvidadoRespondido(convidado) {
    return (
      <div
        key={convidado.id}
        style={{
          ...estilos.convidadoResumoItem,
          background: peopleTheme.itemBg ?? tema.itemBg,
        }}
      >
        <span style={{
          ...estilos.convidadoNome,
          color: peopleTheme.textColor ?? tema.labelColor,
        }}>
          {convidado.nome}
        </span>
        {renderStatusConvidado(convidado)}
      </div>
    );
  }

  function definirRespostaConvidado(convidadoId, status) {
    setRespostasConvidados((prev) => ({
      ...prev,
      [convidadoId]: status,
    }));
  }

  function definirRespostaTodosPendentes(status) {
    setRespostasConvidados((prev) => {
      const next = { ...prev };
      convidadosPendentes.forEach((convidado) => {
        next[convidado.id] = status;
      });
      return next;
    });
  }

  function getRespostaOptionStyle(status, selecionado) {
    if (!selecionado) return {};
    return status === 1 ? estilos.respostaOptionVaiSelecionada : estilos.respostaOptionNaoVaiSelecionada;
  }

  function getRespostaIconStyle(status, selecionado) {
    if (!selecionado) return {};
    return status === 1 ? estilos.respostaIconVaiSelecionado : estilos.respostaIconNaoVaiSelecionado;
  }

  function renderRespostaOption(convidado, status, label, symbol) {
    const selecionado = respostasConvidados[convidado.id] === status;

    return (
      <label
        style={{
          ...estilos.respostaOption,
          ...getRespostaOptionStyle(status, selecionado),
          cursor: enviando ? "wait" : "pointer",
          opacity: enviando ? 0.72 : 1,
        }}
      >
        <input
          type="radio"
          name={`resposta-convidado-${convidado.id}`}
          value={status}
          checked={selecionado}
          disabled={enviando}
          onChange={() => definirRespostaConvidado(convidado.id, status)}
          style={estilos.respostaInput}
        />
        <span style={{
          ...estilos.respostaIcon,
          ...getRespostaIconStyle(status, selecionado),
        }}>
          {symbol}
        </span>
        <span style={estilos.respostaLabel}>{label}</span>
      </label>
    );
  }

  function addPessoa() {
    setPessoas((prev) => [...prev, criarPessoa(novoId())]);
  }

  function removePessoa(id) {
    if (pessoas.length <= 1) return;
    setPessoas((prev) => prev.filter((p) => p.id !== id));
  }

  function updatePessoa(id, campo, valor) {
    setPessoas((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [campo]: valor } : p))
    );
  }

  async function handleSubmitConvidados(e) {
    e.preventDefault();

    const convidadosSemResposta = convidadosPendentes.filter((c) => !respostaConvidadoFoiInformada(respostasConvidados[c.id]));
    if (convidadosSemResposta.length > 0) {
      setAlertMsg("Informe se cada pessoa vai ou não vai.");
      return;
    }

    setEnviando(true);
    setAlertMsg(null);

    let sucessos = 0;
    let recusas = 0;
    const erros = [];
    const recadoConfirmacao = jaExisteRecadoRegistrado ? null : recado || null;

    for (const convidado of convidadosPendentes) {
      const status = respostasConvidados[convidado.id];

      try {
        await axios.post(`${process.env.REACT_APP_API}/confirmacao`, {
          eventId: evento.id,
          nomeConvidado: convidado.nome,
          status,
          idade: 0,
          crianca: false,
          numeroConvidados: 0,
          recado: recadoConfirmacao,
          codigoConfirmacao: convidado.codigoConfirmacao,
          convidadoId: convidado.id,
          convidadoPrincipal: true,
        });
        if (status === 1) {
          sucessos++;
        } else {
          recusas++;
        }
      } catch (err) {
        const msg = err.response?.data?.mensagem ?? err.response?.data?.message ?? "Erro de conexão";
        erros.push(`${convidado.nome}: ${msg}`);
      }
    }

    setEnviando(false);

    if (sucessos > 0) {
      dispararConfete();
    }

    onConcluido({ sucessos, recusas, erros });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const nomeVazio = pessoas.some((p) => !p.nome.trim());
    if (nomeVazio) {
      setAlertMsg("Preencha o nome de todas as pessoas.");
      return;
    }

    setEnviando(true);
    setAlertMsg(null);

    let sucessos = 0;
    const erros = [];

    for (const pessoa of pessoas) {
      try {
        await axios.post(`${process.env.REACT_APP_API}/confirmacao`, {
          eventId: evento.id,
          nomeConvidado: pessoa.nome.trim(),
          status: 1,
          idade: pessoa.crianca ? parseInt(pessoa.idade) || 0 : 0,
          crianca: pessoa.crianca,
          numeroConvidados: 0,
          recado: recado || null,
          codigoConfirmacao: null,
          convidadoId: null,
          convidadoPrincipal: false,
        });
        sucessos++;
      } catch (err) {
        const msg = err.response?.data?.mensagem ?? err.response?.data?.message ?? "Erro de conexão";
        erros.push(`${pessoa.nome}: ${msg}`);
      }
    }

    setEnviando(false);

    if (sucessos > 0) {
      // dispara confete
      dispararConfete();
    }

    onConcluido({ sucessos, recusas: 0, erros });
  }

  if (conviteJaRespondido) {
    const resumo = getResumoConviteRespondido();

    return (
      <div style={{ ...estilos.formSection, ...getRsvpCssVars(tema), color: tema.formTextColor ?? undefined }}>
        <div style={{
          ...estilos.conviteRespondidoCard,
          background: peopleTheme.sectionBg ?? tema.sectionBg,
          color: peopleTheme.textColor ?? tema.labelColor,
          fontFamily: peopleTheme.fontFamily ?? undefined,
        }}>
          <div style={estilos.conviteRespondidoIcon}>{resumo.icon}</div>
          <h2 style={{
            ...estilos.conviteRespondidoTitle,
            ...tema.titulo,
            color: peopleTheme.titleColor ?? tema.titulo?.color,
            fontFamily: peopleTheme.fontFamily ?? tema.titulo?.fontFamily,
          }}>
            {resumo.titulo}
          </h2>
          <p style={{
            ...estilos.conviteRespondidoText,
            color: peopleTheme.textColor ?? tema.labelColor,
          }}>
            {resumo.texto}
          </p>

          <div style={estilos.conviteRespondidoLista}>
            {convidados.map(renderConvidadoRespondido)}
          </div>
          {renderRecadosConvidados(convidados)}
        </div>
      </div>
    );
  }

  if (conviteNominal) {
    return (
      <div style={{ ...estilos.formSection, ...getRsvpCssVars(tema), color: tema.formTextColor ?? undefined }}>
        {alertMsg && (
          <div style={estilos.alertError}>{alertMsg}</div>
        )}

        <form onSubmit={handleSubmitConvidados}>
          <div style={{
            ...estilos.pessoasSection,
            background: peopleTheme.sectionBg ?? tema.sectionBg,
            color: peopleTheme.textColor ?? tema.labelColor,
            fontFamily: peopleTheme.fontFamily ?? undefined,
          }}>
            <div style={estilos.pessoasHeader}>
              <span style={{
                ...estilos.pessoasTitle,
                ...tema.titulo,
                color: peopleTheme.titleColor ?? tema.titulo?.color,
                fontFamily: peopleTheme.fontFamily ?? tema.titulo?.fontFamily,
              }}>
                <span style={estilos.pessoasTitleIcon} aria-hidden="true">👥</span>
                <span>Confirmar Presenças</span>
              </span>
              {convidadosPendentes.length > 1 && (
                <div style={estilos.respostasAtalhos}>
                  <button type="button" onClick={() => definirRespostaTodosPendentes(1)} disabled={enviando} style={{ ...estilos.btnRespostaAtalho, ...estilos.btnRespostaAtalhoVai }}>
                    ✓ Todos vão
                  </button>
                  <button type="button" onClick={() => definirRespostaTodosPendentes(2)} disabled={enviando} style={{ ...estilos.btnRespostaAtalho, ...estilos.btnRespostaAtalhoNaoVai }}>
                    × Todos não vão
                  </button>
                </div>
              )}
            </div>

            <p style={{
              ...estilos.conviteDescricao,
              color: peopleTheme.textColor ?? tema.labelColor,
              margin: "-6px 0 16px",
              textAlign: "left",
            }}>
              {convidadosRespondidos.length > 0
                ? "Confira quem já respondeu e informe a resposta de cada pessoa pendente."
                : ""}
            </p>

            {convidadosRespondidos.length > 0 && (
              <div style={estilos.convidadosGrupo}>
                <p style={{
                  ...estilos.convidadosGrupoTitulo,
                  color: peopleTheme.titleColor ?? tema.titulo?.color,
                }}>
                  Já responderam
                </p>
                {convidadosRespondidos.map(renderConvidadoRespondido)}
                {renderRecadosConvidados(convidadosRespondidos)}
              </div>
            )}

            <div style={estilos.convidadosGrupo}>
              {convidadosRespondidos.length > 0 && (
                <p style={{
                  ...estilos.convidadosGrupoTitulo,
                  color: peopleTheme.titleColor ?? tema.titulo?.color,
                }}>
                  Pendentes
                </p>
              )}

              {convidadosPendentes.map((convidado) => {
                const itemStyle = layoutConvidadoMobile
                  ? { ...estilos.convidadoRespostaItem, ...estilos.convidadoRespostaItemMobile }
                  : estilos.convidadoRespostaItem;
                const infoStyle = layoutConvidadoMobile
                  ? { ...estilos.convidadoRespostaInfo, ...estilos.convidadoRespostaInfoMobile }
                  : estilos.convidadoRespostaInfo;
                const radioGroupStyle = layoutConvidadoMobile
                  ? { ...estilos.respostaRadioGroup, ...estilos.respostaRadioGroupMobile }
                  : estilos.respostaRadioGroup;

                return (
                  <div
                    key={convidado.id}
                    style={{
                      ...itemStyle,
                      background: peopleTheme.itemBg ?? tema.itemBg,
                    }}
                  >
                    <div style={infoStyle}>
                      <span style={{
                        ...estilos.convidadoNome,
                        color: peopleTheme.textColor ?? tema.labelColor,
                      }}>
                        {convidado.nome}
                      </span>
                      {!layoutConvidadoMobile && renderStatusConvidado(convidado)}
                    </div>
                    <div
                      role="radiogroup"
                      aria-label={`Resposta de ${convidado.nome}`}
                      style={radioGroupStyle}
                    >
                      {renderRespostaOption(convidado, 1, "Vai", "✓")}
                      {renderRespostaOption(convidado, 2, "Não vai", "×")}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {convidadosPendentes.length > 0 && !jaExisteRecadoRegistrado && (
            <div style={{
              ...estilos.recadoSection,
              background: messageTheme.sectionBg ?? tema.recadoBg,
              color: messageTheme.textColor ?? tema.sectionTitleColor ?? tema.labelColor,
              fontFamily: messageTheme.fontFamily ?? undefined,
            }}>
              <div style={estilos.formGroup}>
                <label style={{
                  ...estilos.label,
                  color: messageTheme.titleColor ?? tema.sectionTitleColor ?? tema.labelColor,
                  fontFamily: messageTheme.fontFamily ?? undefined,
                }}>
                  💌 Recado (opcional):
                </label>
                <textarea
                  className="rsvp-themed-field rsvp-message-field"
                  value={recado}
                  onChange={(e) => setRecado(e.target.value)}
                  rows={3}
                  placeholder="Deixe um recado carinhoso para Julinha..."
                  maxLength={1000}
                  style={{ ...estilos.input, ...tema.inputExtra, ...messageTheme.input, resize: "vertical", minHeight: 80 }}
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={enviando || convidadosPendentes.length === 0}
            style={{ ...estilos.btnSubmit, ...tema.submit, ...(convidadosPendentes.length === 0 ? estilos.btnDisabled : {}) }}
          >
            {convidadosPendentes.length === 0
              ? "Convite já respondido"
              : enviando
                ? "Registrando..."
                : todosPendentesRespondidos
                  ? "Registrar Respostas"
                  : "Responder Convite"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ ...estilos.formSection, ...getRsvpCssVars(tema), color: tema.formTextColor ?? undefined }}>
      {alertMsg && (
        <div style={estilos.alertError}>{alertMsg}</div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Seção de pessoas */}
        <div style={{
          ...estilos.pessoasSection,
          background: peopleTheme.sectionBg ?? tema.sectionBg,
          color: peopleTheme.textColor ?? tema.labelColor,
          fontFamily: peopleTheme.fontFamily ?? undefined,
        }}>
          <div style={estilos.pessoasHeader}>
            <span style={{
              ...estilos.pessoasTitle,
              ...tema.titulo,
              color: peopleTheme.titleColor ?? tema.titulo?.color,
              fontFamily: peopleTheme.fontFamily ?? tema.titulo?.fontFamily,
            }}>
              <span style={estilos.pessoasTitleIcon} aria-hidden="true">👥</span>
              <span>Confirmar Pessoas</span>
            </span>
            <button type="button" onClick={addPessoa} style={{ ...estilos.btnAdd, ...tema.btnAdd, ...peopleTheme.addButton }}>
              + Adicionar
            </button>
          </div>

          {pessoas.map((p, idx) => (
            <div key={p.id} style={{ ...estilos.pessoaItem, background: peopleTheme.itemBg ?? tema.itemBg }}>
              <div style={estilos.pessoaHeader}>
                <div style={{
                  ...estilos.pessoaNumero,
                  background: peopleTheme.numberBg ?? tema.numeroBg,
                  color: peopleTheme.numberColor ?? tema.numeroColor,
                }}>
                  {idx + 1}
                </div>
                {pessoas.length > 1 && (
                  <button type="button" onClick={() => removePessoa(p.id)} style={estilos.btnRemove}>
                    ×
                  </button>
                )}
              </div>

              <div style={estilos.formRow}>
                <div style={estilos.formGroupNome}>
                  <label style={{ ...estilos.label, color: peopleTheme.textColor ?? tema.labelColor }}>
                    👤 Nome
                  </label>
                  <input
                    className="rsvp-themed-field rsvp-people-field"
                    type="text"
                    value={p.nome}
                    onChange={(e) => updatePessoa(p.id, "nome", e.target.value)}
                    required
                    placeholder="Nome completo"
                    maxLength={200}
                    style={{ ...estilos.input, ...tema.inputExtra, ...peopleTheme.input }}
                  />
                </div>

                <div style={estilos.formGroupCrianca}>
                  <label style={{ ...estilos.label, color: peopleTheme.textColor ?? tema.labelColor }}>👶 É Criança</label>
                  <select
                    className="rsvp-themed-field rsvp-people-field"
                    value={p.crianca ? "sim" : "nao"}
                    onChange={(e) => updatePessoa(p.id, "crianca", e.target.value === "sim")}
                    style={{ ...estilos.input, ...tema.inputExtra, ...peopleTheme.input }}
                  >
                    <option value="nao">Não</option>
                    <option value="sim">Sim</option>
                  </select>
                </div>
              </div>

              {p.crianca && (
                <div style={estilos.formGroup}>
                  <label style={{ ...estilos.label, color: peopleTheme.textColor ?? tema.labelColor }}>🎂 Idade</label>
                  <input
                    className="rsvp-themed-field rsvp-people-field"
                    type="number"
                    value={p.idade}
                    onChange={(e) => updatePessoa(p.id, "idade", e.target.value)}
                    min="0"
                    max="17"
                    placeholder="Idade da criança"
                    style={{ ...estilos.input, ...tema.inputExtra, ...peopleTheme.input }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Seção de recado */}
        <div style={{
          ...estilos.recadoSection,
          background: messageTheme.sectionBg ?? tema.recadoBg,
          color: messageTheme.textColor ?? tema.sectionTitleColor ?? tema.labelColor,
          fontFamily: messageTheme.fontFamily ?? undefined,
        }}>
          <div style={estilos.formGroup}>
            <label style={{
              ...estilos.label,
              color: messageTheme.titleColor ?? tema.sectionTitleColor ?? tema.labelColor,
              fontFamily: messageTheme.fontFamily ?? undefined,
            }}>
              💌 Recado (opcional):
            </label>
            <textarea
              className="rsvp-themed-field rsvp-message-field"
              value={recado}
              onChange={(e) => setRecado(e.target.value)}
              rows={3}
              placeholder="Deixe um recado carinhoso..."
              maxLength={1000}
              style={{ ...estilos.input, ...tema.inputExtra, ...messageTheme.input, resize: "vertical", minHeight: 80 }}
            />
          </div>
        </div>

        <button type="submit" disabled={enviando} style={{ ...estilos.btnSubmit, ...tema.submit }}>
          {enviando ? "Confirmando..." : "Confirmar Todas as Presenças"}
        </button>
      </form>
    </div>
  );
}

function dispararConfete() {
  const shapes = ["🎉", "🎊", "✨", "🎈", "🌟", "💖"];
  for (let i = 0; i < 30; i++) {
    setTimeout(() => {
      const el = document.createElement("div");
      el.innerHTML = shapes[Math.floor(Math.random() * shapes.length)];
      el.style.cssText = `
        position:fixed; left:${Math.random() * 100}vw; top:-20px;
        font-size:25px; pointer-events:none; z-index:9999;
        animation:confeteFall 4s linear forwards;
      `;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 4000);
    }, i * 100);
  }

  if (!document.getElementById("confete-style")) {
    const s = document.createElement("style");
    s.id = "confete-style";
    s.textContent = `@keyframes confeteFall { to { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`;
    document.head.appendChild(s);
  }
}

// ---------- estilos inline do formulário ----------

const estilos = {
  formSection: {
    fontFamily: "'Poppins', sans-serif",
  },
  pessoasSection: {
    background: "rgba(255,255,255,0.15)",
    borderRadius: 15,
    padding: 20,
    marginBottom: 20,
  },
  pessoasHeader: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  pessoasTitle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "white",
    fontWeight: 600,
    fontSize: "1.1em",
    lineHeight: 1.2,
    textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
  },
  pessoasTitleIcon: {
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1,
  },
  btnAdd: {
    background: "linear-gradient(45deg, #ffa726, #ff9800)",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: 20,
    fontWeight: 600,
    cursor: "pointer",
    fontSize: 14,
    boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
  },
  pessoaItem: {
    background: "rgba(255,255,255,0.9)",
    borderRadius: 12,
    padding: 20,
    marginBottom: 15,
    boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
  },
  respostasAtalhos: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: 8,
  },
  btnRespostaAtalho: {
    border: "1px solid rgba(148,163,184,0.32)",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
    lineHeight: 1,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 3px 10px rgba(15,23,42,0.08)",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  btnRespostaAtalhoVai: {
    background: "#ecfdf5",
    color: "#047857",
  },
  btnRespostaAtalhoNaoVai: {
    background: "#fff1f2",
    color: "#be123c",
  },
  convidadoRespostaItem: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 14,
    borderRadius: 14,
    padding: "14px 16px",
    marginBottom: 10,
    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
  },
  convidadoRespostaItemMobile: {
    gridTemplateColumns: "minmax(0, 1fr)",
    alignItems: "stretch",
    gap: 12,
  },
  convidadoRespostaInfo: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 7,
  },
  convidadoRespostaInfoMobile: {
    width: "100%",
    gap: 0,
  },
  respostaRadioGroup: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(88px, 1fr))",
    gap: 8,
    minWidth: 196,
  },
  respostaRadioGroupMobile: {
    width: "100%",
    minWidth: 0,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  respostaOption: {
    minHeight: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    border: "1px solid rgba(148,163,184,0.36)",
    borderRadius: 12,
    padding: "8px 10px",
    background: "rgba(255,255,255,0.72)",
    color: "#475569",
    fontWeight: 800,
    fontSize: 13,
    transition: "border-color 0.2s, box-shadow 0.2s, background 0.2s, color 0.2s",
    userSelect: "none",
  },
  respostaOptionVaiSelecionada: {
    background: "#ecfdf5",
    borderColor: "#34d399",
    color: "#047857",
    boxShadow: "0 7px 18px rgba(16,185,129,0.16)",
  },
  respostaOptionNaoVaiSelecionada: {
    background: "#fff1f2",
    borderColor: "#fb7185",
    color: "#be123c",
    boxShadow: "0 7px 18px rgba(244,63,94,0.14)",
  },
  respostaInput: {
    position: "absolute",
    opacity: 0,
    pointerEvents: "none",
  },
  respostaIcon: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(148,163,184,0.45)",
    background: "rgba(255,255,255,0.9)",
    color: "#94a3b8",
    fontSize: 15,
    fontWeight: 900,
    lineHeight: 1,
  },
  respostaIconVaiSelecionado: {
    background: "#16a34a",
    borderColor: "#16a34a",
    color: "white",
  },
  respostaIconNaoVaiSelecionado: {
    background: "#e11d48",
    borderColor: "#e11d48",
    color: "white",
  },
  respostaLabel: {
    whiteSpace: "nowrap",
  },
  convidadoCheckboxItem: {
    display: "grid",
    gridTemplateColumns: "28px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 12,
    borderRadius: 12,
    padding: "14px 16px",
    marginBottom: 10,
    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
  },
  convidadoCheckbox: {
    width: 20,
    height: 20,
    accentColor: "#16a34a",
    cursor: "pointer",
  },
  convidadoNome: {
    minWidth: 0,
    color: "#1f2937",
    fontWeight: 700,
    overflowWrap: "anywhere",
  },
  convidadoStatus: {
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  convidadoStatusPendente: {
    background: "#fef3c7",
    color: "#92400e",
  },
  convidadoStatusConfirmado: {
    background: "#dcfce7",
    color: "#166534",
  },
  convidadoStatusRecusado: {
    background: "#fee2e2",
    color: "#991b1b",
  },
  convidadoStatusTalvez: {
    background: "#dbeafe",
    color: "#1d4ed8",
  },
  convidadoResumoItem: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 12,
    borderRadius: 12,
    padding: "14px 16px",
    marginBottom: 10,
    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
  },
  convidadosGrupo: {
    marginTop: 14,
  },
  convidadosGrupoTitulo: {
    margin: "0 0 8px",
    fontSize: 13,
    lineHeight: 1.2,
    fontWeight: 800,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  pessoaHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 15,
  },
  pessoaNumero: {
    background: "#f4a6cd",
    color: "white",
    width: 30,
    height: 30,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 14,
  },
  btnRemove: {
    background: "#ff4757",
    color: "white",
    border: "none",
    width: 30,
    height: 30,
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 15,
  },
  formGroupNome: {
    flex: "2 1 160px",
    minWidth: 0,
    marginBottom: 12,
  },
  formGroupCrianca: {
    flex: "1 1 100px",
    minWidth: 0,
    marginBottom: 12,
  },
  formGroup: {
    flex: "1 1 100%",
    marginBottom: 12,
  },
  label: {
    display: "block",
    marginBottom: 6,
    fontWeight: 600,
    color: "#333",
    fontSize: 14,
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "2px solid #ddd",
    borderRadius: 8,
    fontSize: 15,
    background: "white",
    boxSizing: "border-box",
    fontFamily: "inherit",
    outline: "none",
  },
  inputDisabled: {
    background: "rgba(255,255,255,0.72)",
    color: "#475569",
    cursor: "not-allowed",
  },
  conviteCard: {
    background: "rgba(255,255,255,0.86)",
    border: "1px solid rgba(255,255,255,0.72)",
    borderRadius: 18,
    padding: 24,
    textAlign: "center",
    boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
  },
  conviteSaudacao: {
    fontSize: 22,
    lineHeight: 1.25,
    fontWeight: 700,
    color: "#1f2937",
    margin: 0,
  },
  convitePergunta: {
    fontSize: 18,
    lineHeight: 1.4,
    color: "#475569",
    margin: "12px 0 0",
  },
  conviteDescricao: {
    fontSize: 14,
    color: "#64748b",
    margin: "10px 0 0",
  },
  conviteRespondidoCard: {
    borderRadius: 18,
    padding: 28,
    marginBottom: 20,
    textAlign: "center",
    boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
  },
  conviteRespondidoIcon: {
    fontSize: 44,
    lineHeight: 1,
    marginBottom: 14,
  },
  conviteRespondidoTitle: {
    margin: 0,
    fontSize: 26,
    lineHeight: 1.15,
    fontWeight: 800,
  },
  conviteRespondidoText: {
    margin: "12px 0 0",
    fontSize: 15,
    lineHeight: 1.5,
  },
  conviteRespondidoLista: {
    marginTop: 22,
    textAlign: "left",
  },
  recadoRespondidoBox: {
    borderRadius: 14,
    padding: "14px 16px",
    marginTop: 16,
    textAlign: "left",
    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
  },
  recadoRespondidoTitulo: {
    margin: "0 0 8px",
    fontSize: 13,
    lineHeight: 1.2,
    fontWeight: 800,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  recadoRespondidoTexto: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
  conviteActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginTop: 22,
  },
  btnResposta: {
    border: "none",
    borderRadius: 14,
    padding: "13px 18px",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnRespostaSim: {
    background: "#16a34a",
    color: "white",
  },
  btnRespostaNao: {
    background: "#f1f5f9",
    color: "#475569",
  },
  recadoSection: {
    background: "rgba(255,255,255,0.1)",
    borderRadius: 15,
    padding: 20,
    marginBottom: 20,
  },
  btnSubmit: {
    background: "linear-gradient(45deg, #ffa726, #ff6b6b)",
    color: "white",
    padding: "16px 30px",
    border: "none",
    borderRadius: 25,
    cursor: "pointer",
    fontSize: 17,
    fontWeight: 600,
    width: "100%",
    textTransform: "uppercase",
    letterSpacing: 1,
    boxShadow: "0 5px 15px rgba(255,107,107,0.4)",
  },
  btnDisabled: {
    opacity: 0.62,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  alertError: {
    background: "#f8d7da",
    color: "#721c24",
    border: "1px solid #f5c6cb",
    borderRadius: 10,
    padding: 15,
    marginBottom: 16,
    fontSize: 14,
  },
};
